import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SearchableMenu, { SEARCHABLE_MENU_ROW_CLASS } from '../../ui/SearchableMenu'
import Text from '../../ui/Text'

const FULL_NAME_KEY = 'full_name'
const RESET_LABEL_CLASS = 'tw:text-sm tw:text-gray-900'
const NAME_WRAPPER_CLASS = 'tw:flex-1 tw:min-w-0 tw:text-left'
const NAME_CLASS = 'tw:text-sm tw:text-gray-900'
const CHECKBOX_CLASS = 'tw:ds-checkbox!'

const filterByFullName = user => user[FULL_NAME_KEY]

const arraysEqual = (left, right) => {
  if (left.length !== right.length)
    return false
  const setRight = new Set(right)
  return left.every(value => setRight.has(value))
}

const AttributionInlineEditor = ({
  anchorEl = null, availableUsers, currentUserIds,
  isOpen, onApply, onClose
}) => {
  const [selectedIds, setSelectedIds] = useState(currentUserIds)
  const [orderedUsers, setOrderedUsers] = useState(availableUsers)
  const initialIdsRef = useRef(currentUserIds)
  const wasOpenRef = useRef(false)

  // Reset the selection to the current attribution on each open transition.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened)
      return
    initialIdsRef.current = currentUserIds
    setSelectedIds(currentUserIds)
  }, [isOpen, currentUserIds])

  // Float the selected users to the top. Recompute when the list arrives, which
  // Can happen after the menu opens (lazy fetch) — otherwise the first open stays empty.
  useEffect(() => {
    if (!isOpen)
      return
    const selected = new Set(initialIdsRef.current)
    setOrderedUsers(
      [...availableUsers].sort((left, right) =>
        (selected.has(left.id) ? 0 : 1) - (selected.has(right.id) ? 0 : 1))
    )
  }, [isOpen, availableUsers])

  const handleCloseWithCommit = useCallback(() => {
    if (!arraysEqual(initialIdsRef.current, selectedIds))
      onApply(selectedIds)
    onClose()
  }, [onApply, onClose, selectedIds])

  const handleToggle = useCallback(event => {
    const id = Number(event.currentTarget.dataset.userId)
    setSelectedIds(previous => {
      if (previous.includes(id))
        return previous.filter(value => value !== id)
      return [...previous, id]
    })
  }, [])

  const handleReset = useCallback(() => {
    setSelectedIds([])
  }, [])

  const renderUser = useCallback(user => {
    const checked = selectedIds.includes(user.id)
    return (
      <button
        key={user.id}
        aria-pressed={checked}
        className={SEARCHABLE_MENU_ROW_CLASS}
        data-user-id={user.id}
        type="button"
        onClick={handleToggle}
      >
        <span aria-hidden="true" className={CHECKBOX_CLASS} data-checked={checked} />
        <span className={NAME_WRAPPER_CLASS}>
          <Text lines={1} className={NAME_CLASS}>{user[FULL_NAME_KEY]}</Text>
        </span>
      </button>
    )
  }, [selectedIds, handleToggle])

  const resetHeader = (
    <button
      className={SEARCHABLE_MENU_ROW_CLASS}
      type="button"
      onClick={handleReset}
    >
      <span className={RESET_LABEL_CLASS}>Aucune sélection</span>
    </button>
  )

  return (
    <SearchableMenu
      anchorEl={anchorEl}
      ariaLabel="Modifier l’attribution"
      filterBy={filterByFullName}
      header={resetHeader}
      isOpen={isOpen}
      items={orderedUsers}
      renderItem={renderUser}
      searchName="attribution-editor-search"
      widthClass="tw:w-72"
      onClose={handleCloseWithCommit}
    />
  )
}

AttributionInlineEditor.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  availableUsers: PropTypes.arrayOf(PropTypes.shape({
    [FULL_NAME_KEY]: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  })).isRequired,
  currentUserIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

AttributionInlineEditor.defaultProps = {
  anchorEl: null
}

export default AttributionInlineEditor
